import { Spacing } from '@persona-id/design-tokens';
import { CellContext, ColumnDef, HeaderContext } from '@tanstack/react-table';
import { keyBy } from 'lodash';
import { lowerCase, upperFirst } from 'lodash/fp';
import { DateTime } from 'luxon';
import { Link } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import TransactionStatusSticker from 'dashboard/components/TransactionStatusSticker';
import { Tag } from 'dashboard/models/tag';
import { Transaction } from 'dashboard/models/transaction';
import { TransactionStatus } from 'dashboard/models/transaction-status';
import { TransactionType } from 'dashboard/models/transaction-type';
import Checkbox from 'design-system/components/Checkbox';
import Sticker from 'design-system/components/Sticker';
import { DATE_AND_TIME_FORMAT } from 'lib/constants/luxonFormats';
import { FieldSchema, FieldsMap } from 'lib/fields';
import { ApiListRelationship, ApiModel, ApiRelationship } from 'lib/interfaces/api';

const TagSticker = styled(Sticker)`
  margin-right: ${Spacing[2]};
`;

export interface TransactionColumnDef {
  id: string;
  type: ApiRelationship<ApiModel.TransactionType>;
  'created-at': string;
  status: ApiRelationship<ApiModel.TransactionStatus>;
  tags: ApiListRelationship<ApiModel.Tag>;
  fields: FieldsMap;
}

export const generateColumnIdToLabel = ({
  transactionTypes,
}: {
  transactionTypes: TransactionType[];
}) => {
  const transactionTypeFieldSchemas = transactionTypes.flatMap(
    (tt) => tt.attributes['field-schemas'],
  );

  const attributeKeyToLabelMapping = {
    id: 'ID',
    'created-at': 'Created at',
    status: 'Status',
    tags: 'Tags',
    type: 'Type',
  };

  const fieldSchemaKeyToLabelMapping = transactionTypeFieldSchemas.reduce(
    (accum: Record<string, string>, fs) => {
      // NB: Not all Transaction type field schemas have labels. Fallback to the humanizing field schema key if no label exists.
      accum[fs.key] = fs.label ? fs.label : upperFirst(lowerCase(fs.key));
      return accum;
    },
    {},
  );
  return {
    ...attributeKeyToLabelMapping,
    ...fieldSchemaKeyToLabelMapping,
  };
};

interface GenerateColumnDefsProps {
  columnIdToLabel: Record<string, string>;
  tags: Tag[];
  transactionStatuses: TransactionStatus[];
  transactionTypes: TransactionType[];
}

export const generateColumnDefs = ({
  columnIdToLabel,
  tags,
  transactionStatuses,
  transactionTypes,
}: GenerateColumnDefsProps): ColumnDef<TransactionColumnDef, any>[] => {
  const tagsByKey = keyBy(tags, (tag: Tag) => tag.id);
  const transactionStatusesByKey = keyBy(transactionStatuses, (ts: TransactionStatus) => ts.id);
  const transactionTypesByKey = keyBy(transactionTypes, (tt: TransactionType) => tt.id);
  const transactionTypeFieldSchemas = transactionTypes.flatMap(
    (tt) => tt.attributes['field-schemas'],
  );

  const standardColumns: ColumnDef<TransactionColumnDef, any>[] = [
    {
      id: 'select',
      header: ({ table }: HeaderContext<TransactionColumnDef, any>) => (
        <Checkbox
          {...{
            checked: table.getIsAllRowsSelected(),
            indeterminate: table.getIsSomeRowsSelected(),
            onChange: table.getToggleAllRowsSelectedHandler(),
          }}
        />
      ),
      cell: ({ row }: CellContext<TransactionColumnDef, any>) => (
        <Checkbox {...{ checked: row.getIsSelected(), onChange: row.getToggleSelectedHandler() }} />
      ),
      enableSorting: false,
    },
    {
      id: 'id',
      accessorKey: 'id',
      cell: ({ cell }: CellContext<TransactionColumnDef, any>) => (
        <Link to={`/dashboard/transactions/${cell.getValue()}`}>{cell.getValue()}</Link>
      ),
      header: columnIdToLabel['id'],
    },
    {
      id: 'type',
      accessorKey: 'type',
      cell: ({ cell }: CellContext<TransactionColumnDef, any>) => {
        const transactionType = cell.getValue();
        return transactionTypesByKey[transactionType.data.id].attributes.name;
      },
      header: columnIdToLabel['type'],
    },
    {
      id: 'created-at',
      accessorKey: 'created-at',
      cell: ({ cell }: CellContext<TransactionColumnDef, any>) =>
        DateTime.fromISO(cell.getValue()).toFormat(DATE_AND_TIME_FORMAT),
      header: columnIdToLabel['created-at'],
    },
    {
      id: 'status',
      accessorKey: 'status',
      cell: ({ cell }: CellContext<TransactionColumnDef, any>) => (
        <TransactionStatusSticker
          transactionStatus={transactionStatusesByKey[cell.getValue().data.id]}
        />
      ),
      header: columnIdToLabel['status'],
    },
    {
      id: 'tags',
      accessorKey: 'tags',
      cell: ({ cell }: CellContext<TransactionColumnDef, any>) => {
        const tags = cell.getValue();
        return tags.data.map(({ id }: Tag) => (
          <TagSticker key={id}>{tagsByKey[id].attributes.name}</TagSticker>
        ));
      },
      header: columnIdToLabel['tags'],
    },
  ];

  const fieldSchemaColumnDefs: ColumnDef<TransactionColumnDef, any>[] =
    transactionTypeFieldSchemas.map((fs: FieldSchema) => ({
      id: `${fs.key}`,
      header: columnIdToLabel[fs.key],
      accessorFn: (row) => (fs.key in row.fields ? row.fields[fs.key].value : 'None'),
    }));

  return [...standardColumns, ...fieldSchemaColumnDefs];
};

interface GenerateTableDataProps {
  transactions: Transaction[];
}

export const generateTableData = ({
  transactions,
}: GenerateTableDataProps): TransactionColumnDef[] => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return transactions.map(
    ({
      id,
      attributes: { 'created-at': createdAt, fields },
      relationships: { tags, 'transaction-type': type, 'transaction-status': status },
    }) => ({
      id: id,
      type: type,
      'created-at': createdAt,
      status: status,
      tags: tags,
      fields: fields,
    }),
  );
};
